/**
 * `store/library` — `spec.md` §13.9's library slice: "favorites set, playlists, recently
 * played, catalogue pages (keyed by filter signature), search results".
 *
 * ## The favourites map is the client's single source of truth
 *
 * Every `TrackSummary` the API returns carries `isFavorite`, so the same fact arrives
 * embedded in dozens of rows across the catalogue, search, history and queue. Trusting
 * those copies means a heart tapped on the Now Playing screen does not light up in the
 * crate behind it, and "fix it by walking every cached page" is how a store becomes
 * unmaintainable.
 *
 * So: `favorites` is an id-keyed map that every hydrate seeds from the rows it receives,
 * {@link selectIsFavorite} is the only sanctioned read, and a toggle updates exactly one
 * place. Rows are rendered from the map, never from `track.isFavorite`.
 *
 * It is a `Record<string, true>` rather than a `Set` because §13.9 requires the slices to
 * stay serialisable and a `Set` does not survive `JSON.stringify`.
 *
 * ## Catalogue pages are keyed by a filter signature
 *
 * The crate is filterable and cursor-paged (§6.1: cursors, never offsets). Two different
 * filters are two different lists that must not append into each other, so each is stored
 * under {@link filterSignature} — a canonical, order-independent key. Paging appends;
 * changing a filter starts a new list; nothing is ever offset-indexed.
 *
 * ## Search supersedes
 *
 * Typing produces overlapping requests whose answers can arrive out of order. Each new
 * search aborts the one before it, and the response is additionally checked against the
 * echoed `query` field (§6.5 puts it in the payload for exactly this reason) before it is
 * allowed to replace what is on screen.
 */

import { create } from 'zustand';

import type {
  ArtistSummary,
  CatalogueResponse,
  CatalogueSection,
  HistoryItem,
  PlaylistHeader,
  SearchBackend,
  SearchGroupName,
  SearchGroups,
  TagSummary,
  TrackSummary,
} from '@bl/shared';

import type { ApiClient, ApiFailure, CatalogueArgs, FavoritesArgs } from '../lib/api';
import { api, isApiErrorCode, toApiFailure } from '../lib/api';

export type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

/** One cursor-paged list. `nextCursor === null` means the end, not "not loaded yet". */
export interface PagedList<T> {
  items: T[];
  nextCursor: string | null;
  /** Present only when the endpoint returns a real `COUNT(*)` (§6.1). */
  total: number | null;
  status: LoadStatus;
  /** `true` while a *subsequent* page is loading, so the footer can show the ruler. */
  loadingMore: boolean;
  error: ApiFailure | null;
}

export interface CataloguePage extends PagedList<TrackSummary> {
  /** The drop dividers for the rows loaded so far (§6.4). */
  sections: CatalogueSection[];
}

export interface SearchResults {
  /** The query as typed, so a stale response can be discarded. */
  query: string;
  /** What the server actually searched with, after `normalizeText`. May be `''`. */
  normalized: string;
  backend: SearchBackend | null;
  groups: SearchGroups;
  status: LoadStatus;
  error: ApiFailure | null;
}

export interface LibraryData {
  /** Id-keyed favourite set. See the header. */
  favorites: Record<string, true>;
  favoritesPage: PagedList<TrackSummary>;
  playlists: PlaylistHeader[];
  playlistsStatus: LoadStatus;
  playlistsError: ApiFailure | null;
  history: PagedList<HistoryItem>;
  /** Catalogue lists keyed by {@link filterSignature}. */
  catalogue: Record<string, CataloguePage>;
  search: SearchResults;
  /** The drawer index, shared by the search zero state and the Artist/Tag screens. */
  artists: PagedList<ArtistSummary>;
  tags: PagedList<TagSummary>;
  /** `true` when the server has answered `503 INDEX_NOT_READY` (§6.4). */
  indexNotReady: boolean;
}

export interface LibraryActions {
  /** Load (or reload) the first page of a catalogue filter. */
  loadCatalogue(args?: CatalogueArgs): Promise<void>;
  /** Append the next page of that filter. A no-op at the end of the list. */
  loadMoreCatalogue(args?: CatalogueArgs): Promise<void>;
  loadFavorites(args?: FavoritesArgs): Promise<void>;
  loadMoreFavorites(args?: FavoritesArgs): Promise<void>;
  /** Optimistic; rolled back on failure. Returns the resulting flag. */
  toggleFavorite(trackId: number): Promise<boolean>;
  loadPlaylists(): Promise<void>;
  createPlaylist(title: string, trackIds?: readonly number[]): Promise<PlaylistHeader | null>;
  deletePlaylist(id: number): Promise<void>;
  loadHistory(): Promise<void>;
  loadArtists(q?: string): Promise<void>;
  loadTags(): Promise<void>;
  /** Debouncing belongs to the screen; supersession belongs here. */
  runSearch(query: string, groups?: readonly SearchGroupName[]): Promise<void>;
  clearSearch(): void;
  /** Seed the favourites map from any batch of rows. */
  noteTracks(tracks: readonly { id: number; isFavorite: boolean }[]): void;
  reset(): void;
}

export type LibraryState = LibraryData & LibraryActions;

function emptyList<T>(): PagedList<T> {
  return {
    items: [],
    nextCursor: null,
    total: null,
    status: 'idle',
    loadingMore: false,
    error: null,
  };
}

const EMPTY_SEARCH: SearchResults = {
  query: '',
  normalized: '',
  backend: null,
  groups: {},
  status: 'idle',
  error: null,
};

function emptyData(): LibraryData {
  return {
    favorites: {},
    favoritesPage: emptyList<TrackSummary>(),
    playlists: [],
    playlistsStatus: 'idle',
    playlistsError: null,
    history: emptyList<HistoryItem>(),
    catalogue: {},
    search: { ...EMPTY_SEARCH },
    artists: emptyList<ArtistSummary>(),
    tags: emptyList<TagSummary>(),
    indexNotReady: false,
  };
}

/**
 * A canonical key for a catalogue filter.
 *
 * Keys are sorted, `undefined` values are dropped, and the result is deterministic —
 * `{ sort: 'recent', year: 1998 }` and `{ year: 1998, sort: 'recent' }` are one list, not
 * two. `cursor` is excluded on purpose: it identifies a *position* in a list, not the
 * list, and including it would make every page its own list.
 */
export function filterSignature(args: CatalogueArgs = {}): string {
  const parts: string[] = [];
  for (const key of Object.keys(args).sort()) {
    if (key === 'cursor') continue;
    const value = (args as Record<string, string | number | boolean | undefined>)[key];
    if (value === undefined) continue;
    parts.push(`${key}=${String(value)}`);
  }
  return parts.length > 0 ? parts.join('&') : 'sort=recent';
}

function favoritesFrom(
  current: Record<string, true>,
  tracks: readonly { id: number; isFavorite: boolean }[],
): Record<string, true> {
  let changed = false;
  const next: Record<string, true> = { ...current };
  for (const track of tracks) {
    const key = String(track.id);
    if (track.isFavorite && next[key] !== true) {
      next[key] = true;
      changed = true;
    } else if (!track.isFavorite && next[key] === true) {
      delete next[key];
      changed = true;
    }
  }
  return changed ? next : current;
}

/** Append a page, dropping ids we already hold: a cursor page can legally overlap. */
function appendUnique<T extends { id: number }>(
  existing: readonly T[],
  incoming: readonly T[],
): T[] {
  return appendUniqueAt(existing, incoming).items;
}

/** {@link appendUniqueAt}'s answer: the combined list, and where each incoming row landed. */
export interface AppendedPage<T> {
  readonly items: T[];
  /**
   * `positions[i]` is the index `incoming[i]` occupies in {@link AppendedPage.items} — the
   * index it *already* had when an overlapping cursor page made it a duplicate.
   */
  readonly positions: number[];
}

/**
 * Append a page and report where every incoming row landed.
 *
 * {@link rebaseSections} needs exactly this: a section's `afterIndex` points into the page
 * it was computed against, and that is not the array the crate renders.
 */
export function appendUniqueAt<T extends { id: number }>(
  existing: readonly T[],
  incoming: readonly T[],
): AppendedPage<T> {
  const at = new Map<number, number>();
  existing.forEach((item, index) => at.set(item.id, index));

  const items = [...existing];
  const positions: number[] = [];
  for (const item of incoming) {
    const already = at.get(item.id);
    if (already !== undefined) {
      positions.push(already);
      continue;
    }
    at.set(item.id, items.length);
    positions.push(items.length);
    items.push(item);
  }
  return { items, positions };
}

/**
 * Rebase an appended page's `sections` onto the combined list.
 *
 * The server computes `afterIndex` against **the page it just returned**, so the first run
 * of every page is `-1` — "before the first item of this page" (`computeSections`,
 * `apps/api/src/db/queries/tracks.ts`). Appended verbatim, page two's day rules therefore
 * claim positions at the head of the *combined* list: `buildCrateRows` prints every
 * `afterIndex: -1` section before the first track, so the crate showed two date rules back
 * to back with no rows between them, then more of them scattered through page one.
 *
 * Every run is therefore re-derived from where its own rows actually landed — first and
 * last, through `positions` — which also survives an overlapping cursor page, where some
 * of the run is already held. A day that a page boundary (or an overlap) split is folded
 * back into the one rule it always was, and its count is the span it really covers, never
 * the sum of two page-local counts.
 */
export function rebaseSections(
  existing: readonly CatalogueSection[],
  incoming: readonly CatalogueSection[],
  positions: readonly number[],
  base: number,
): CatalogueSection[] {
  const out = [...existing];

  for (const section of incoming) {
    /* `afterIndex` is `runStart - 1`, so the run spans page rows `afterIndex + 1 … + count`. */
    const first = positions[section.afterIndex + 1] ?? base + section.afterIndex + 1;
    const last = positions[section.afterIndex + section.count] ?? first + section.count - 1;
    if (last < first) continue;

    const previous = out[out.length - 1];
    if (
      previous !== undefined &&
      previous.kind === 'day' &&
      section.kind === 'day' &&
      previous.label === section.label &&
      first <= previous.afterIndex + previous.count + 1
    ) {
      /* One day, split by a page boundary or by an overlap: one rule, one span. Only
         `day` — two different drops can share a date, and nothing here tells them apart. */
      out[out.length - 1] = { ...previous, count: last - previous.afterIndex };
      continue;
    }

    out.push({ ...section, afterIndex: first - 1, count: last - first + 1 });
  }

  return out;
}

export interface LibraryStoreDeps {
  client?: ApiClient;
}

export function createLibraryStore(deps: LibraryStoreDeps = {}) {
  const client = deps.client ?? api;

  return create<LibraryState>()((set, get) => {
    /** The in-flight search, so the next keystroke can cancel it. */
    let searchAbort: AbortController | null = null;

    const patchCatalogue = (signature: string, patch: Partial<CataloguePage>): void => {
      const existing = get().catalogue[signature] ?? { ...emptyList<TrackSummary>(), sections: [] };
      set({ catalogue: { ...get().catalogue, [signature]: { ...existing, ...patch } } });
    };

    const adoptCataloguePage = (
      signature: string,
      response: CatalogueResponse,
      append: boolean,
    ): void => {
      const existing = get().catalogue[signature] ?? { ...emptyList<TrackSummary>(), sections: [] };
      const base = existing.items.length;
      const appended = appendUniqueAt(existing.items, response.items);
      patchCatalogue(signature, {
        items: append ? appended.items : [...response.items],
        nextCursor: response.nextCursor,
        total: response.total,
        sections: append
          ? rebaseSections(existing.sections, response.sections, appended.positions, base)
          : [...response.sections],
        status: 'ready',
        loadingMore: false,
        error: null,
      });
      get().noteTracks(response.items);
    };

    const noteFailure = (error: unknown): ApiFailure => {
      if (isApiErrorCode(error, 'INDEX_NOT_READY')) set({ indexNotReady: true });
      return toApiFailure(error);
    };

    return {
      ...emptyData(),

      noteTracks(tracks): void {
        const next = favoritesFrom(get().favorites, tracks);
        if (next !== get().favorites) set({ favorites: next });
      },

      async loadCatalogue(args = {}): Promise<void> {
        const signature = filterSignature(args);
        patchCatalogue(signature, { status: 'loading', error: null });
        try {
          const response = await client.catalogue(args);
          set({ indexNotReady: false });
          adoptCataloguePage(signature, response, false);
        } catch (error) {
          patchCatalogue(signature, { status: 'error', error: noteFailure(error) });
        }
      },

      async loadMoreCatalogue(args = {}): Promise<void> {
        const signature = filterSignature(args);
        const page = get().catalogue[signature];
        if (page === undefined || page.nextCursor === null || page.loadingMore) return;
        patchCatalogue(signature, { loadingMore: true });
        try {
          const response = await client.catalogue({ ...args, cursor: page.nextCursor });
          adoptCataloguePage(signature, response, true);
        } catch (error) {
          patchCatalogue(signature, { loadingMore: false, error: noteFailure(error) });
        }
      },

      async loadFavorites(args = {}): Promise<void> {
        set({ favoritesPage: { ...get().favoritesPage, status: 'loading', error: null } });
        try {
          const response = await client.favorites(args);
          set({
            favoritesPage: {
              items: [...response.items],
              nextCursor: response.nextCursor,
              total: response.total,
              status: 'ready',
              loadingMore: false,
              error: null,
            },
          });
          get().noteTracks(response.items);
        } catch (error) {
          set({
            favoritesPage: {
              ...get().favoritesPage,
              status: 'error',
              error: noteFailure(error),
            },
          });
        }
      },

      async loadMoreFavorites(args = {}): Promise<void> {
        const page = get().favoritesPage;
        if (page.nextCursor === null || page.loadingMore) return;
        set({ favoritesPage: { ...page, loadingMore: true } });
        try {
          const response = await client.favorites({ ...args, cursor: page.nextCursor });
          set({
            favoritesPage: {
              items: appendUnique(get().favoritesPage.items, response.items),
              nextCursor: response.nextCursor,
              total: response.total,
              status: 'ready',
              loadingMore: false,
              error: null,
            },
          });
          get().noteTracks(response.items);
        } catch (error) {
          set({
            favoritesPage: {
              ...get().favoritesPage,
              loadingMore: false,
              error: noteFailure(error),
            },
          });
        }
      },

      async toggleFavorite(trackId): Promise<boolean> {
        const key = String(trackId);
        const was = get().favorites[key] === true;
        const next = { ...get().favorites };
        if (was) delete next[key];
        else next[key] = true;
        set({ favorites: next });

        try {
          if (was) await client.removeFavorite(trackId);
          else await client.addFavorite(trackId);

          // Keep the MY CRATE page consistent with the toggle without re-fetching it:
          // a removal drops the row, an addition is left to the next load (we have no
          // `TrackSummary` to insert and will not invent one).
          if (was) {
            const page = get().favoritesPage;
            const items = page.items.filter((item) => item.id !== trackId);
            if (items.length !== page.items.length) {
              set({
                favoritesPage: {
                  ...page,
                  items,
                  total: page.total === null ? null : Math.max(0, page.total - 1),
                },
              });
            }
          }
          return !was;
        } catch (error) {
          const rolledBack = { ...get().favorites };
          if (was) rolledBack[key] = true;
          else delete rolledBack[key];
          set({
            favorites: rolledBack,
            favoritesPage: { ...get().favoritesPage, error: toApiFailure(error) },
          });
          return was;
        }
      },

      async loadPlaylists(): Promise<void> {
        set({ playlistsStatus: 'loading', playlistsError: null });
        try {
          const response = await client.playlists();
          set({ playlists: [...response.items], playlistsStatus: 'ready' });
        } catch (error) {
          set({ playlistsStatus: 'error', playlistsError: noteFailure(error) });
        }
      },

      async createPlaylist(title, trackIds): Promise<PlaylistHeader | null> {
        try {
          const body: { title: string; trackIds?: number[] } = { title };
          if (trackIds !== undefined) body.trackIds = [...trackIds];
          const created = await client.createPlaylist(body);
          set({ playlists: [created, ...get().playlists] });
          return created;
        } catch (error) {
          set({ playlistsError: toApiFailure(error) });
          return null;
        }
      },

      async deletePlaylist(id): Promise<void> {
        const before = get().playlists;
        set({ playlists: before.filter((playlist) => playlist.id !== id) });
        try {
          await client.deletePlaylist(id);
        } catch (error) {
          set({ playlists: before, playlistsError: toApiFailure(error) });
        }
      },

      async loadHistory(): Promise<void> {
        set({ history: { ...get().history, status: 'loading', error: null } });
        try {
          const response = await client.history();
          set({
            history: {
              items: [...response.items],
              nextCursor: response.nextCursor,
              total: response.total ?? null,
              status: 'ready',
              loadingMore: false,
              error: null,
            },
          });
          get().noteTracks(response.items);
        } catch (error) {
          set({ history: { ...get().history, status: 'error', error: noteFailure(error) } });
        }
      },

      async loadArtists(q): Promise<void> {
        set({ artists: { ...get().artists, status: 'loading', error: null } });
        try {
          const response = await client.artists(q === undefined ? {} : { q });
          set({
            artists: {
              items: [...response.items],
              nextCursor: response.nextCursor,
              total: response.total,
              status: 'ready',
              loadingMore: false,
              error: null,
            },
          });
        } catch (error) {
          set({ artists: { ...get().artists, status: 'error', error: noteFailure(error) } });
        }
      },

      async loadTags(): Promise<void> {
        set({ tags: { ...get().tags, status: 'loading', error: null } });
        try {
          const response = await client.tags();
          set({
            tags: {
              items: [...response.items],
              nextCursor: response.nextCursor,
              total: response.total,
              status: 'ready',
              loadingMore: false,
              error: null,
            },
          });
        } catch (error) {
          set({ tags: { ...get().tags, status: 'error', error: noteFailure(error) } });
        }
      },

      async runSearch(query, groups): Promise<void> {
        searchAbort?.abort();
        const controller = new AbortController();
        searchAbort = controller;

        set({ search: { ...get().search, query, status: 'loading', error: null } });

        try {
          const args = groups === undefined ? { q: query } : { q: query, groups };
          const response = await client.search(args, { signal: controller.signal });

          // Out-of-order answer for a query the user has already moved on from.
          if (get().search.query !== query) return;

          set({
            search: {
              query,
              normalized: response.normalized,
              backend: response.backend,
              groups: response.groups,
              status: 'ready',
              error: null,
            },
          });
          const hits = response.groups.tracks?.items ?? [];
          if (hits.length > 0) get().noteTracks(hits);
        } catch (error) {
          // An abort is our own doing: the newer request owns the state now.
          if (isApiErrorCode(error, 'ABORTED')) return;
          if (get().search.query !== query) return;
          set({ search: { ...get().search, status: 'error', error: noteFailure(error) } });
        } finally {
          if (searchAbort === controller) searchAbort = null;
        }
      },

      clearSearch(): void {
        searchAbort?.abort();
        searchAbort = null;
        set({ search: { ...EMPTY_SEARCH } });
      },

      reset(): void {
        searchAbort?.abort();
        searchAbort = null;
        set({ ...emptyData() });
      },
    };
  });
}

/** The app-wide library slice. */
export const useLibraryStore = createLibraryStore();

/* ────────────────────────────────────────────────────────────────────────────
 * Selectors
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The only sanctioned favourite read. Never `track.isFavorite` — see the header.
 *
 * Takes the narrowest slice it actually needs rather than the whole `LibraryData`, so a
 * screen that has SUBSCRIBED to `favorites` (which is how a virtualised ledger repaints
 * its brackets) can hand that value straight in. Any `LibraryData` still satisfies it,
 * so every existing call site is unchanged — the point is that the alternative was
 * re-writing `state.favorites[String(id)] === true` at the call site, which is the one
 * thing this selector exists to prevent.
 */
export const selectIsFavorite =
  (trackId: number) =>
  (state: Pick<LibraryData, 'favorites'>): boolean =>
    state.favorites[String(trackId)] === true;

export const selectFavoriteCount = (state: LibraryData): number =>
  Object.keys(state.favorites).length;

export const selectCataloguePage =
  (args: CatalogueArgs = {}) =>
  (state: LibraryData): CataloguePage | null =>
    state.catalogue[filterSignature(args)] ?? null;

export const selectCatalogueItems =
  (args: CatalogueArgs = {}) =>
  (state: LibraryData): TrackSummary[] =>
    state.catalogue[filterSignature(args)]?.items ?? [];

/** `true` when a filter has a page loaded and it is empty — the EMPTY-state condition. */
export const selectCatalogueIsEmpty =
  (args: CatalogueArgs = {}) =>
  (state: LibraryData): boolean => {
    const page = state.catalogue[filterSignature(args)];
    return page !== undefined && page.status === 'ready' && page.items.length === 0;
  };

export const selectPlaylistById =
  (id: number) =>
  (state: LibraryData): PlaylistHeader | null =>
    state.playlists.find((playlist) => playlist.id === id) ?? null;

export const selectSearchTrackHits = (state: LibraryData): TrackSummary[] =>
  state.search.groups.tracks?.items ?? [];

/**
 * `true` when the search ran and every requested group came back empty.
 *
 * The distinction §6.5 draws matters here: an *absent* group was not asked for, an
 * *empty* one was asked for and matched nothing. Only the second is a zero-result state.
 */
export const selectSearchIsEmpty = (state: LibraryData): boolean => {
  if (state.search.status !== 'ready') return false;
  const { tracks, artists, tags, years } = state.search.groups;
  const counts = [
    tracks?.items.length ?? 0,
    artists?.items.length ?? 0,
    tags?.items.length ?? 0,
    years?.items.length ?? 0,
  ];
  return counts.every((count) => count === 0);
};

/** The recently-played list, ready for the `/history` screen. */
export const selectRecentlyPlayed = (state: LibraryData): HistoryItem[] => state.history.items;
